import React, { FC, memo, useCallback, useContext } from 'react'
import { StyledGhostButton, CTAButton } from '../styles'
import { ThemeContext } from 'styled-components'
import { useTranslation } from '../../../hooks/locale'
import { Typography } from 'react-components'
import get from 'lodash/get'
import useDynamicQueryParams from '../hooks/use-dynamic-query-params'
import { FormattedBookingTypeData } from '../../../models/property-details-page/booking-type-model'
import { SearchbarFieldsReturn, UpdateSearchBarTransport } from '../hooks/use-searchbar'
import getChildrenAges from '../utils/get-children-ages'
import AnalyticsService from '../../../services/analytics-service'
import { pdpPageActions } from '../../../services/analytics-service/actions'
import { FormattedLivePricingDataTransport } from '../../../models/pricing/live-pricing-formatter'
import InquiryContactDetailsTransport from '../../../transports/inquiry-contact-details-transport'
import UserDetailsModel from '../../../models/user/user-details-model'
import { PdpToCheckoutPosition } from '../constants'
import { useABVariant } from '../../../utils/hooks'
import { AB_KEY, AB_VARIANT_VERSION } from '../../../common/constants'
import { usePropertyDetailsContext } from '../../../hooks/property-details'
import { getCategory } from '../../../utils/category-utils'
import { useRouter } from 'next/router'
import { useEnquiryPopup } from '../../../hooks/use-enquiry-popup'

type ContactButtonProps = {
    bookingType: FormattedBookingTypeData | null
    searchbar: SearchbarFieldsReturn
    pricingDetails: FormattedLivePricingDataTransport | null
    inquiryContactDetails: InquiryContactDetailsTransport | null
    isPricingLoading: boolean
    isAvailabilityLoading: boolean
    pricingErrorCode: number | null
    updateSearchbar: UpdateSearchBarTransport
    availableDates: Date[] | null
    userDetails: UserDetailsModel | null
    petsAllowed: boolean
}

const ContactButton: FC<ContactButtonProps> = props => {
    const theme = useContext(ThemeContext)
    const { t } = useTranslation()
    const { query } = useRouter()
    const { bookingType, searchbar, ...restProps } = props

    const dynamicQueryParams = JSON.parse(get(bookingType, 'subType.dynamicQueryParams', '{}'))
    const childrenAges = getChildrenAges(searchbar.kids)
    const qpValues = { ...query, ...searchbar, childrenAges }
    const url = useDynamicQueryParams(bookingType?.subType, qpValues, dynamicQueryParams)
    const type = get(bookingType, 'subType.type')
    const { isComboListing } = usePropertyDetailsContext()

    const { isEnquiryPopupOpen, EnquiryPopup, showEnquiryPopup, hideEnquiryPopup } = useEnquiryPopup()

    const { isProvidedVariantActive } = useABVariant(AB_KEY.CHECKOUT_MERGER, AB_VARIANT_VERSION.ONE)

    const onContactButtonClick = useCallback(
        (event: React.MouseEvent) => {
            AnalyticsService.trackEvent({
                category: getCategory(isComboListing),
                action: pdpPageActions.CONTACT_ENTER_DETAILS_BUTTON_CLICKED,
            })

            if (type === 'redirect') {
                event.preventDefault()
                window.open(url, '_blank')
            } else {
                if (isProvidedVariantActive) {
                    event.preventDefault()
                    showEnquiryPopup()
                } else {
                    window.location.href = url
                }
            }
        },
        [type, url, showEnquiryPopup, isProvidedVariantActive, isComboListing],
    )

    return (
        <>
            <CTAButton contentAlignment={theme.style.justifyContent.flexStart} margin={'1rem 0 0 0'}>
                <StyledGhostButton isPrimary={false} isContentWidth onClick={onContactButtonClick}>
                    <Typography variant="button" fontWeight="regular">
                        {t('contact.contactEnterDetails')}
                    </Typography>
                </StyledGhostButton>
            </CTAButton>
            {!!EnquiryPopup && (
                <EnquiryPopup
                    isOpen={isEnquiryPopupOpen}
                    onClose={hideEnquiryPopup}
                    searchbar={searchbar}
                    position={PdpToCheckoutPosition.CONTACT_BUTTON}
                    {...restProps}
                />
            )}
        </>
    )
}

export default memo(ContactButton)
